import { OpenFeature, TypedInMemoryProvider } from '@openfeature/react-sdk';
import {
    featureService,
    DEFAULT_FEATURES,
    KNOWN_FEATURES,
} from './featureService';

/**
 * Registering the feature flags the server reports.
 *
 * Kept apart from shared/contexts/FeatureFlagContext.js, which every gated
 * component imports: this module reaches api.js and therefore Firebase, and
 * dragging that into every consumer of useFeature is what broke the client
 * suite the first time round. Only index.js imports this.
 *
 * The page does not wait for the server's answer, and that is what shapes this
 * module. One provider is registered synchronously, before the first render,
 * with every flag off; the answer arrives later and is pushed into that same
 * provider as a configuration change.
 *
 * Replacing the provider instead would be the obvious way to write this and it
 * does not work. Registering a provider builds a fresh event emitter for the
 * domain, so handlers the mounted hooks added against the previous one are
 * orphaned: the swap raises no event they can hear, every flag stays off for
 * the life of the page, and nothing anywhere reports a problem. Changing the
 * configuration of a provider that is already registered emits
 * ConfigurationChanged on the emitter those hooks are actually listening to,
 * which is the SDK's supported path for flags that arrive after the render and
 * the one thing here that must not be refactored casually. T-FF-6 pins it.
 */

// Every flag this client knows, on or off, so a flag we recognise never falls
// through to the caller's default. It also makes the update below a real diff:
// ConfigurationChanged reports which keys changed, and a flag that was absent
// rather than explicitly off would be a weaker signal to the hooks watching.
function flagConfiguration(enabled) {
    return KNOWN_FEATURES.reduce((flags, name) => {
        flags[name] = {
            variants: { on: true, off: false },
            defaultVariant: enabled.includes(name) ? 'on' : 'off',
            disabled: false,
        };
        return flags;
    }, {});
}

// A name only one side recognises means the two have drifted. Dropping it
// keeps the client's behaviour predictable; saying so keeps the drift from
// being invisible, which is the failure the server refuses to boot over.
function recognised(names) {
    const unknown = names.filter((name) => !KNOWN_FEATURES.includes(name));
    if (unknown.length > 0) {
        console.warn(
            `Ignoring feature flag(s) this client does not recognise: ${unknown.join(
                ', '
            )}. The server and client flag names have drifted.`
        );
    }
    return names.filter((name) => KNOWN_FEATURES.includes(name));
}

/**
 * Register the provider, then fill it in from the server.
 *
 * Call once during bootstrap, before rendering, and do not await it: the
 * registration happens synchronously, before this function's first await, so
 * the provider is in place by the time the caller renders. Everything after
 * that is the network, which nothing waits for.
 *
 * Never rejects. A failed, slow, or malformed response leaves the all-off
 * configuration exactly as registered, because the cost of that is a hidden
 * feature, while the cost of the opposite is an unfinished one on screen.
 *
 * @returns {Promise<string[]>} the flags that were enabled.
 */
export async function initializeFeatureFlags() {
    // Synchronous, and deliberately ahead of every await below: the caller
    // renders as soon as this returns its promise, and a hook that mounts
    // before any provider is registered would attach its handlers to an
    // emitter this provider then replaces.
    const provider = new TypedInMemoryProvider(
        flagConfiguration(DEFAULT_FEATURES)
    );
    OpenFeature.setProvider(provider);

    try {
        const remote = await featureService.getFeatures();
        const enabled = Array.isArray(remote?.features)
            ? recognised(remote.features)
            : DEFAULT_FEATURES;

        // Same provider, new configuration. This is what reaches components
        // that are already on screen.
        await provider.putConfiguration(flagConfiguration(enabled));

        return enabled;
    } catch {
        // The provider stays as registered: every flag off.
        return DEFAULT_FEATURES;
    }
}

export default initializeFeatureFlags;
